/**
 * The account's library, as a conversation sees it.
 *
 * Two jobs beyond forwarding to the store. The first is that **nothing arriving from a
 * peer is taken on trust**: the bytes are parsed as an STL here, locally, and a file that
 * does not parse is refused rather than written and discovered later by the renderer. The
 * geometry the library records is what this parse found, not what the sender claimed.
 *
 * The second is serialising writes. Several conversations run at once during one share,
 * and IndexedDB will happily interleave two read-modify-writes into a duplicate record.
 * Everything that changes the library goes through one queue.
 */

import { logger } from '../log.js';
import { readModelFile } from '../readModel.js';
import type { Library } from './conversation.js';
import type {
  Amendment,
  Manifest,
  ManifestEntry,
  ModelHash,
  Tombstone,
} from '../domain/sync.js';
import type { ModelId, ModelMeta } from '../domain/types.js';
import type { ModelStore } from '../storage/modelStore.js';

const log = logger('share');

export interface LibraryEvents {
  readonly onAdded: (model: ModelMeta) => void;
  readonly onRemoved: (ids: readonly ModelId[]) => void;
  /** Models whose colour or size was changed elsewhere, and must be redrawn here. */
  readonly onRevised: (ids: readonly ModelId[]) => void;
}

export class StoreLibrary implements Library {
  /** Every write, in order. See the note at the top. */
  private work: Promise<unknown> = Promise.resolve();

  private accepted = 0;
  private removed = 0;
  private revised = 0;
  private refused = 0;

  constructor(
    private readonly store: ModelStore,
    private readonly owner: string,
    private readonly events: LibraryEvents,
  ) {}

  get counts(): { accepted: number; removed: number; revised: number; refused: number } {
    return {
      accepted: this.accepted,
      removed: this.removed,
      revised: this.revised,
      refused: this.refused,
    };
  }

  async manifest(): Promise<Manifest> {
    return this.store.manifestFor(this.owner);
  }

  async bytesFor(hash: ModelHash): Promise<Blob | undefined> {
    return this.store.getFileByHash(hash, this.owner);
  }

  async accept(entry: ManifestEntry, blob: Blob): Promise<void> {
    // Parsed here rather than believed. A peer is meant to be another session of the same
    // account, but "meant to be" is not something the receiving code can check, and a file
    // that is not a model would otherwise be found out by the renderer, long after it had
    // been written into the library and offered onwards to every other session.
    //
    // Identified by its content, not by the name it arrives under: that name was chosen on
    // another machine, and nothing here has any reason to trust it.
    const parsed = await readModelFile(await blob.arrayBuffer());
    if (!parsed.ok) {
      this.refused += 1;
      log.warn(`Refused ${entry.name} from another session: ${parsed.message}`);
      return;
    }

    await this.queue(async () => {
      // Two conversations can push the same model at once, and the plan that said it was
      // missing was made before either arrived.
      if (await this.store.getFileByHash(entry.hash, this.owner)) return;

      const meta = await this.store.importModel(entry, blob, parsed.stats, this.owner);
      this.accepted += 1;
      this.events.onAdded(meta);
    });
  }

  async forget(tombstones: readonly Tombstone[]): Promise<void> {
    await this.queue(async () => {
      const gone = await this.store.applyTombstones(tombstones, this.owner);
      if (gone.length === 0) return;

      this.removed += gone.length;
      this.events.onRemoved(gone);
    });
  }

  /**
   * Take on a colour or a size decided in another session.
   *
   * Nothing is parsed or refused here, unlike an arriving file: no bytes are involved, the
   * store checks each value before it writes it, and a decision that does not survive that
   * check is simply not applied. What is reported is the models that actually changed, so
   * that one on display can be repainted at once rather than after the next refresh.
   */
  async revise(amendments: readonly Amendment[]): Promise<void> {
    await this.queue(async () => {
      const changed = await this.store.applyAmendments(amendments, this.owner);
      if (changed.length === 0) return;

      this.revised += changed.length;
      this.events.onRevised(changed);
    });
  }

  /**
   * Run a piece of work after everything queued before it.
   *
   * A failed write must not stall the queue behind it, so the chain continues past a
   * rejection while the caller still sees it.
   */
  private async queue<T>(work: () => Promise<T>): Promise<T> {
    const result = this.work.then(work);
    this.work = result.catch(() => undefined);
    return result;
  }
}
